import React, {
  useEffect,
  useMemo,
} from 'react';
import MediaPreview from 'pages/Dashboard/components/MediaPreview';
import isNil from 'lodash/isNil';
import {
  MultimediaTypeWithCategory,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/FileUploadDialog';
import {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import Tag from 'core/components/Tag';
import clsx from 'clsx';
import head from 'lodash/head';
import {
  useSelectableContext,
} from 'core/hooks';

type Props = {
 data?: MultimediaTypeWithCategory[];
 onUpdate: (original: MultimediaTypeWithCategory, files: FileLocalUrl[]) => void;
};

export default function Preview({
  data,
  onUpdate,
}: Props) {
  const { selected, toggle } = useSelectableContext();

  const filteredData = useMemo(
    () => (data?.filter((item) => (item?.files?.length ?? 0) > 0) ?? []),
    [data],
  );

  const previewData = useMemo(
    () => data?.find((item) => item?.label === head(Array.from(selected))),
    [data, selected],
  );

  const previewFiles = useMemo(() => (
    (previewData?.files ?? [])
      .filter((file): file is FileLocalUrl & { file: File } => !isNil(file.file))
      .map((file) => ({
        file,
        src: URL.createObjectURL(file.file),
      }))
  ), [previewData?.files]);

  useEffect(() => {
    if (filteredData?.length > 0 && selected?.size === 0) {
      toggle(head(filteredData)?.label ?? '');
    }
  }, [filteredData, selected]);

  useEffect(() => () => {
    previewFiles.forEach(({ src }) => URL.revokeObjectURL(src));
  }, [previewFiles]);

  return (
    <div className="flex flex-col h-full px-4 pb-2 overflow-auto">
      {filteredData?.length > 0 && (
        <div className="sticky top-0 z-10 flex flex-wrap gap-2 bg-gray-50 py-3">
          {filteredData?.map((item) => {
            const isSelected = previewData?.label === item?.label;
            return (
              <div key={item?.label} className="flex flex-col gap-1">
                <Tag
                  tagName={`${item?.label ?? ''} (${item?.files?.length ?? 0})`}
                  className={clsx('font-semibold', {
                    '!bg-blue-100 !text-blue-700': isSelected,
                    '!bg-gray-200 !text-gray-700': !isSelected,
                  })}
                  onClick={() => {
                    if (!isSelected) {
                      toggle(item?.label ?? '');
                    }
                  }}
                />
              </div>
            );
          })}
        </div>
      )}
      {!isNil(previewData) && (
        <div className="flex flex-col gap-1 flex-1">
          {previewFiles.map(({ file, src }, fileIndex) => (
            <MediaPreview
              key={`${previewData?.label}-${file?.file?.name}-${file?.id}`}
              media={{
                url: { value: src },
                fileName: file.file?.name,
              }}
              thumbnail
              onDelete={() => {
                const files = previewData?.files ?? [];
                onUpdate(previewData, [
                  ...files.slice(0, fileIndex),
                  ...files.slice(fileIndex + 1),
                ]);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
